import { TextInput } from '@/components/ui/text-input';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useRouter } from 'expo-router';
import React from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { PageTitle } from '@/components/seo/page-title';
import { useEditProfileForm } from './hooks/use-edit-profile-form';
import { useProfilePhotoUpload } from './hooks/use-profile-photo-upload';

export function EditProfileScreen() {
  const theme = useAppTheme();
  const router = useRouter();
  const form = useEditProfileForm();
  const photo = useProfilePhotoUpload();

  const profileImage = photo.profileImage || form.profileImage;
  const isUploading = photo.isUploading;

  if (form.profileLoading || !form.user) {
    return (
      <SafeAreaView className="flex-1">
        <View className="px-4 py-2 flex-row items-center justify-between border-b border-border">
          <View className="w-10 h-10" />
          <Text className="text-lg font-semibold text-foreground" accessibilityRole="header">
            Edit Profile
          </Text>
          <View className="px-4 py-2">
            <Text className="font-medium text-muted-foreground">Save</Text>
          </View>
        </View>
        <View className="flex-1 justify-center items-center">
          <ActivityIndicator size="large" className="text-foreground" />
          <Text className="mt-2 text-muted-foreground">Loading profile...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1">
      <PageTitle title="Edit Profile" />
      <KeyboardAvoidingView
        behavior={process.env.EXPO_OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
        keyboardVerticalOffset={process.env.EXPO_OS === 'ios' ? 90 : 0}
      >
        <View className="px-4 py-2 flex-row items-center justify-between border-b border-border">
          <Pressable
            onPress={() => router.back()}
            className="w-10 h-10 items-center justify-center"
            accessibilityRole="button"
            accessibilityLabel="Go back"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="arrow-back" size={24} className="text-foreground" />
          </Pressable>
          <Text className="text-lg font-semibold text-foreground" accessibilityRole="header">
            Edit Profile
          </Text>
          <Pressable
            onPress={form.handleSave}
            className="px-4 py-2"
            disabled={form.isSaving || isUploading}
            accessibilityRole="button"
            accessibilityLabel="Save profile"
          >
            {form.isSaving ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Text className={`font-medium ${(form.isSaving || isUploading) ? 'text-muted-foreground' : 'text-primary'}`}>
                Save
              </Text>
            )}
          </Pressable>
        </View>

        <ScrollView className="flex-1" showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16 }}>
          <View className="items-center mb-6">
            <View className="relative">
              <View
                className="overflow-hidden border-border"
                style={{ width: 96, height: 96, borderRadius: 48, borderWidth: 1.5, borderCurve: 'continuous', backgroundColor: 'transparent' }}
              >
                {profileImage ? (
                  <Image
                    source={{ uri: profileImage }}
                    style={{ width: '100%', height: '100%', borderRadius: 48 }}
                    resizeMode="cover"
                    accessibilityLabel="Your profile photo"
                  />
                ) : (
                  <View className="w-full h-full bg-surface items-center justify-center">
                    <Ionicons name="person-circle" size={77} className="text-muted-foreground" />
                  </View>
                )}
                {isUploading && (
                  <View className="absolute inset-0 w-24 h-24 rounded-full bg-black/50 items-center justify-center">
                    <ActivityIndicator size="small" color="#fff" />
                  </View>
                )}
              </View>
              <Pressable
                onPress={photo.handleChangeProfileImage}
                className="absolute bottom-0 right-0 w-11 h-11 rounded-full items-center justify-center bg-primary"
                disabled={isUploading}
                style={{ opacity: isUploading ? 0.5 : 1 }}
                accessibilityRole="button"
                accessibilityLabel="Change profile photo"
              >
                <Ionicons name="camera" size={20} />
              </Pressable>
            </View>
          </View>

          <TextInput
            label="Name"
            containerClassName="mb-4"
            value={form.name}
            onChangeText={form.setName}
            placeholder="Your full name"
            autoCapitalize="words"
          />

          <View className="mb-4">
            <TextInput
              label="Username"
              value={form.username}
              onChangeText={form.handleUsernameChange}
              placeholder="Your username"
              autoCapitalize="none"
              autoCorrect={false}
              leftIcon={<Text className="text-muted-foreground" style={{ fontSize: 15 }}>@</Text>}
              rightIcon={
                form.checkingUsername ? (
                  <ActivityIndicator size="small" className="text-muted-foreground" />
                ) : form.usernameAvailable === true && form.username !== form.originalUsername ? (
                  <Text className="text-green-500 text-lg">✓</Text>
                ) : form.usernameAvailable === false ? (
                  <Text className="text-red-500 text-lg">✕</Text>
                ) : undefined
              }
              status={
                form.usernameAvailable === false
                  ? 'error'
                  : form.usernameAvailable === true && form.username !== form.originalUsername
                    ? 'success'
                    : undefined
              }
              inputContainerClassName="bg-card"
            />
            {form.username.length >= 3 && form.username !== form.originalUsername && (
              <Text
                accessibilityLiveRegion="polite"
                className={`text-xs mt-2 ${
                  form.checkingUsername
                    ? 'text-muted-foreground opacity-50'
                    : form.usernameAvailable === true
                      ? 'text-success'
                      : form.usernameAvailable === false
                        ? 'text-error'
                        : 'text-muted-foreground'
                }`}
              >
                {form.checkingUsername
                  ? 'Checking availability...'
                  : form.usernameAvailable === true
                    ? 'Username is available!'
                    : form.usernameAvailable === false
                      ? 'Username is already taken'
                      : ''}
              </Text>
            )}
            {form.username.length > 0 && form.username.length < 3 && (
              <Text className="text-xs mt-2 text-muted-foreground">
                Username must be at least 3 characters long
              </Text>
            )}
          </View>

          <TextInput
            label="Bio"
            containerClassName="mb-4"
            value={form.bio}
            onChangeText={form.setBio}
            placeholder="Tell us about yourself"
            multiline
            numberOfLines={5}
            autoCapitalize="sentences"
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
